import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { ApiError } from "@tdelivr/api-client";

import { productAttributes, productGlyph, quoteKey } from "../booking.ts";
import {
  askingNear,
  cancelKey,
  countdownState,
  exhaustion,
  freshnessOf,
  SEARCH_REREAD_INTERVAL_MS,
  searchChip,
  searchPhase,
  searchTimeLeft,
} from "../dispatch-search.ts";
import { quoteMoney } from "../money.ts";
import {
  CANCELLED_BY_RIDER,
  type DispatchProcess,
  type RideProduct,
  type RideQuote,
  type RiderApi,
  type RideRequestRecord,
} from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { Hero, SearchHero } from "./Hero.tsx";
import { Absent, Note, PhoneShell, Rows, type ActionProps } from "./PhoneShell.tsx";
import { StateScreen } from "./StateScreen.tsx";

/**
 * `5 Finding` and `5b No driver` — one dispatch process, and its `status` decides which.
 *
 * The same shape as `Quote.tsx`: the design draws two screens, the platform publishes one record,
 * and the record's own field says which screen a rider is on. A rider who opens `?page=no-driver` on
 * a search that is still running is shown `5 Finding`, and the address is corrected to agree; a
 * rider sitting on `5 Finding` when the search gives up is moved to `5b`. Neither is a decision this
 * application makes — `status` is DSP-04's.
 *
 * ## These two screens existed as recorded absences for most of this application's life
 *
 * DSP-04 computed `status`, `attempt`, `maximumAttempts`, `deadlineAt` and `failureReason` from the
 * day it was written and published them **only inside the 202 of its event-ingestion `POST`**,
 * behind `dispatch:orchestrate`. So the only caller that could read a dispatch's progress was one
 * entitled to drive the saga, and an earlier agent declined to route these two screens rather than
 * put a fabricated countdown in front of a rider. `GET /v1/tenants/{tenantId}/dispatch-processes/
 * {demandId}` under `dispatch:read` —
 * [ADR 0031](../../../docs/adr/0031-the-dispatch-process-is-read-under-its-own-scope-and-bounded-by-the-tenant-because-the-record-has-no-subject.md)
 * — closed it, and nothing had to be invented because every field was already in the row.
 *
 * ## What each screen draws, and the field behind it
 *
 * | Drawn                                   | From                                                |
 * | --------------------------------------- | --------------------------------------------------- |
 * | `finding_candidates · try 1 of 3`       | DSP-04 `status`, `attempt`, `maximumAttempts`        |
 * | `Finding your driver`                   | designed copy                                       |
 * | `Asking drivers near Westlands.`        | GEO-02 on MOB-03's `quote.origin`                   |
 * | `Search time left 1:48`                 | DSP-04 `deadlineAt`, against this phone's clock      |
 * | `Cancel for free`                       | MOB-03 `POST /v1/ride-requests/{id}/cancel`         |
 * | `No XL drivers are free right now`      | DSP-04 `failureReason`, MOB-01's `displayName`      |
 * | `We asked every nearby driver, 3 times` | DSP-04 `attempt` on the failed process              |
 * | `Try Standard · KES 1,450`              | MOB-01's catalogue, MOB-02's `quote.fare.totalMinor` |
 * | `4 min` on that row                     | **nothing** — the same absence `2 Choose` measures   |
 * | `Schedule for later`                    | **nothing** — MOB-03 takes a `quoteId` and no time   |
 *
 * ## Four reads, and the second is the one that is easy to miss
 *
 * DSP-04's process is the subject. **MOB-03's own `GET /v1/ride-requests/{id}`** is read beside it,
 * under ADR 0028's `ride-request:self`, for four things nothing else publishes: the `version` the
 * cancel requires as `expectedVersion`, the `quote.product.id` that becomes the `XL` in the
 * exhausted screen's heading, and the `quote.origin` and `quote.destination` that GEO-02 and MOB-02
 * need for the pickup's name and for the alternative class's price. `RideRequestRecord` in
 * `rider-api.ts` writes that list out. MOB-01's catalogue and MOB-02's prices are the third and
 * fourth, and both are read **only** once the search has actually run out — a rider watching a
 * search that is going fine should not have quotes minted behind the screen for classes they have
 * not been offered.
 *
 * ## The countdown is live, and the screen re-reads
 *
 * The design draws a countdown, an attempt out of a ceiling and a turning radar, which is a screen
 * that moves. Nothing delivers a dispatch transition to this application unasked (RTM-001), so
 * following it is re-reading — which `CLAUDE.md` section 2 names in as many words: _"A 'check
 * status' action is a read, never a retry of the command."_ `dispatch-search.ts` holds the interval,
 * the staleness window and the reasoning for both, because both are this build's numbers.
 *
 * Four states the design does not draw are reachable here and each is built rather than collapsed
 * into the happy path:
 *
 *   - **the deadline has passed and DSP-04 still says the search is running.** The countdown stops
 *     rather than going negative, and the sheet says which of the two possible facts is missing: an
 *     event the orchestrator has not ingested, or a clock on this phone that is wrong.
 *   - **the answer has gone stale**, because re-reads are failing. What was last read stays on
 *     screen — throwing away the only answer there is would leave a rider staring at nothing — and
 *     the screen says how old it is instead of presenting it as current.
 *   - **404.** DSP-04's own words: _"A dispatch that has not started and one that does not exist
 *     answer alike."_ So this is not an error and is not drawn as one.
 *   - **the request was cancelled.** Reachable by pressing `Cancel for free` and by a rider
 *     cancelling from another device, and in both cases "Finding your driver" would be a lie about
 *     something that definitely happened.
 */

/** Which of the two screens the address asked for. */
export type SearchPageId = "finding" | "no-driver";

export interface FindingProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
  readonly tenantId: string;
  readonly customerId: string;
  /** The ride request, which **is** DSP-04's `demandId`. Absent on a link that carries no ride. */
  readonly rideRequestId: string | undefined;
  readonly asked: SearchPageId;
  /** Corrects the address when DSP-04's `status` disagrees with it. */
  readonly onScreenChange: (pageId: string) => void;
  /** Opens `3 Quote` on the alternative class a rider chose from the exhausted screen. */
  readonly onQuoteChosen: (quoteId: string) => void;
  readonly onBack: () => void;
  /** Milliseconds now, injected so the countdown can be put into a known state. */
  readonly now: () => number;
}

export function Finding(props: FindingProps): ReactNode {
  const { api, tenantId, customerId, rideRequestId, now } = props;

  const [process, setProcess] = useState<DispatchProcess | undefined>(undefined);
  /** When this application last **got** an answer, which is what staleness is measured against. */
  const [observedAt, setObservedAt] = useState<number | undefined>(undefined);
  const [notStarted, setNotStarted] = useState(false);
  const [request, setRequest] = useState<RideRequestRecord["request"] | undefined>(undefined);
  const [pickup, setPickup] = useState<{ locality?: string; formatted?: string } | null>(null);
  const [products, setProducts] = useState<readonly RideProduct[]>([]);
  const [quotes, setQuotes] = useState<ReadonlyMap<string, RideQuote>>(new Map());
  const [unpriced, setUnpriced] = useState<ReadonlySet<string>>(new Set());
  const [state, setState] = useState<ViewState>({ kind: "loading" });
  const [tick, setTick] = useState(() => now());
  const [cancelling, setCancelling] = useState(false);

  /*
   * Whether there is already an answer on screen, as a ref rather than as state.
   *
   * Read by the re-read below to decide whether a refusal replaces the screen or merely lets it go
   * stale. It cannot be `process` itself: `read` would then change identity on every successful
   * answer, and the effect that owns the interval would tear it down and start a new one each time —
   * so the re-read would drift later and later rather than keeping its period.
   */
  const hasAnswer = useRef(false);

  const read = useCallback(
    (again: boolean) => {
      if (rideRequestId === undefined) return;
      if (!again) setState({ kind: "loading" });
      api
        .dispatchProcess({ tenantId, demandId: rideRequestId })
        .then((answer) => {
          setProcess(answer);
          setObservedAt(now());
          setNotStarted(false);
          setState({ kind: "ready" });
          hasAnswer.current = true;
        })
        .catch((error: unknown) => {
          /*
           * A 404 is not an error here, and DSP-04's own contract is why: "No dispatch process
           * exists for this demand. A dispatch that has not started and one that does not exist
           * answer alike." A booking whose dispatch has not been started yet is the common case for
           * the first moment of this screen, and `stateForError` would draw it as "That didn't
           * work".
           */
          if (error instanceof ApiError && error.category === "not_found") {
            setNotStarted(true);
            setState({ kind: "ready" });
            return;
          }
          // A failed re-read keeps what is on screen and lets it age into the stale state. Replacing
          // a true-a-moment-ago search with an error screen would lose the only answer there is.
          if (again && hasAnswer.current) return;
          setState(stateForError(error, [SCOPES.dispatchProcess]));
        });
    },
    [api, tenantId, rideRequestId, now],
  );

  useEffect(() => {
    hasAnswer.current = false;
    read(false);
  }, [read]);

  /*
   * MOB-03's own record, for the four facts `RideRequestRecord` lists.
   *
   * Read once per ride rather than on the re-read timer: a ride request's version only moves when
   * somebody cancels it, and its quote never moves at all. A stale version is not a silent problem —
   * MOB-03 refuses the cancel with a conflict, which is what `expectedVersion` is for — so the
   * cancel re-reads it rather than this screen polling for it.
   */
  useEffect(() => {
    if (rideRequestId === undefined) return;
    if (!props.scopes.has(SCOPES.ownRequest)) return;
    let current = true;
    api
      .rideRequest(rideRequestId)
      .then((answer) => {
        if (current) setRequest(answer.request);
      })
      .catch(() => {
        if (current) setRequest(undefined);
      });
    return (): void => {
      current = false;
    };
  }, [api, rideRequestId, props.scopes]);

  /*
   * `Westlands` — the pickup, as GEO-02 names it.
   *
   * The coordinate is the one MOB-02 priced the ride from, published on MOB-03's own record, so the
   * accuracy sent is zero for the reason `3 Quote` states: it is a point a rider chose rather than a
   * satellite fix, and claiming a radius would be inventing a number for a required field.
   */
  useEffect(() => {
    const origin = request?.quote.origin;
    if (origin === undefined) return;
    if (!props.scopes.has(SCOPES.reverseGeocode)) return;
    let current = true;
    api
      .reverseGeocode({ latitude: origin.latitude, longitude: origin.longitude, accuracyMeters: 0 })
      .then((answer) => {
        if (!current) return;
        setPickup({
          ...(answer.address.locality === undefined ? {} : { locality: answer.address.locality }),
          formatted: answer.address.formatted,
        });
      })
      .catch(() => {
        if (current) setPickup(null);
      });
    return (): void => {
      current = false;
    };
  }, [api, request, props.scopes]);

  const phase = process === undefined ? undefined : searchPhase(process.status);
  const exhausted = phase === "exhausted";

  /*
   * MOB-01's catalogue, read **only** once the search has run out.
   *
   * `5 Finding` draws no class list, so a rider watching a search that is going fine has no use for
   * one. The join it is for is `5b`'s two: the display name of the class they booked, which is the
   * `XL` in the heading, and the alternative class the design offers instead.
   */
  useEffect(() => {
    if (!exhausted) return;
    if (!props.scopes.has(SCOPES.products)) return;
    let current = true;
    api
      .rideProducts()
      .then((answer) => {
        if (current) setProducts(answer);
      })
      .catch(() => {
        if (current) setProducts([]);
      });
    return (): void => {
      current = false;
    };
  }, [api, exhausted, props.scopes]);

  /*
   * A price for each class the rider did **not** book, so the cheapest can be offered.
   *
   * The same write `2 Choose` makes, under the same derived key, and bounded the same way: MOB-02's
   * `POST` mints an immutable priced resource, so the key is `booking.ts`'s `quoteKey` — the
   * customer, the product and the two points — and asking twice asks for the same quote rather than
   * minting a second. ADR 0026 section 1 is why the application does this rather than EXP-002.
   *
   * Only on the exhausted screen, and only for classes other than the one that failed: re-pricing
   * the class no driver was free in would be minting a quote for the thing that just did not work.
   */
  const bookedProductId = request?.quote.product.id;
  const origin = request?.quote.origin;
  const destination = request?.quote.destination;
  useEffect(() => {
    if (!exhausted || origin === undefined || destination === undefined) return;
    if (!props.scopes.has(SCOPES.quote)) return;
    let current = true;
    for (const product of products) {
      if (product.lifecycle !== "active") continue;
      if (product.productId === bookedProductId) continue;
      const key = quoteKey({ customerId, productId: product.productId, origin, destination });
      api
        .createQuote({ productId: product.productId, origin, destination }, key)
        .then((quote) => {
          if (!current) return;
          setQuotes((held) => new Map(held).set(product.productId, quote));
        })
        .catch(() => {
          if (!current) return;
          setUnpriced((held) => new Set(held).add(product.productId));
        });
    }
    return (): void => {
      current = false;
    };
  }, [api, exhausted, products, bookedProductId, origin, destination, customerId, props.scopes]);

  /*
   * The countdown, once a second, and the re-read on its own slower period.
   *
   * Both only while the search is running. A terminal process does not move again — DSP-04's
   * `completed` and `failed` are terminal in its own domain — so polling one would be asking the
   * same question for as long as a rider left the screen open, and a countdown on a search that has
   * ended has nothing left to count.
   */
  const running = phase === "searching";
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => setTick(now()), 1000);
    return (): void => clearInterval(timer);
  }, [running, now]);

  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => read(true), SEARCH_REREAD_INTERVAL_MS);
    return (): void => clearInterval(timer);
  }, [running, read]);

  /*
   * `status` decides the screen, and the address is corrected to agree with it.
   *
   * In an effect rather than during render, for the reason `Quote.tsx` states: correcting the
   * address is a parent state change, and a child performing one while rendering is the React fault
   * that shows as a warning in development and a dropped update in production.
   *
   * `completed` is the one that is not a correction but a step: DSP-04 has bound a driver, which is
   * `6 Driver coming`'s subject rather than this screen's, and the address already carries the
   * `?ride=` that screen reads.
   */
  useEffect(() => {
    if (phase === undefined) return;
    const shouldBe =
      phase === "assigned" ? "driver-coming" : phase === "exhausted" ? "no-driver" : "finding";
    if (shouldBe !== props.asked) props.onScreenChange(shouldBe);
  }, [phase, props]);

  const cancel = useCallback(() => {
    if (rideRequestId === undefined) return;
    setCancelling(true);
    /*
     * Re-read the version immediately before cancelling, rather than cancelling against the one this
     * screen loaded.
     *
     * A rider may have sat on this screen for a minute, and `expectedVersion` is the guard against
     * cancelling a row that has moved since. Reading it first is not belt and braces: it is the
     * difference between a conflict a rider can act on and a conflict they cannot, because the only
     * thing that moves a ride request's version is a cancellation — so a stale version here means
     * the ride is already cancelled, and saying so is better than refusing.
     */
    api
      .rideRequest(rideRequestId)
      .then((answer) => {
        setRequest(answer.request);
        if (answer.request.status === "cancelled") {
          setCancelling(false);
          return undefined;
        }
        return api
          .cancelRide({
            rideRequestId,
            reasonCode: CANCELLED_BY_RIDER,
            expectedVersion: answer.request.version,
            idempotencyKey: cancelKey(rideRequestId, answer.request.version),
          })
          .then(() => {
            setCancelling(false);
            // Re-read rather than assuming: the screen the rider sees next is decided by what the
            // services say happened, which is this application's rule everywhere else.
            read(true);
            return undefined;
          });
      })
      .catch((error: unknown) => {
        setCancelling(false);
        /*
         * A 409 means the version moved between the read and the cancel, and the only thing that
         * moves a ride request's version is a cancellation. So the ride is already cancelled, and
         * re-reading says that in the screen above rather than reporting a conflict a rider can do
         * nothing about — `StateScreen`'s conflict wording is about a booking made twice, which is
         * the opposite mistake.
         */
        if (error instanceof ApiError && error.category === "conflict") {
          api
            .rideRequest(rideRequestId)
            .then((answer) => setRequest(answer.request))
            .catch(() => undefined);
          read(true);
          return;
        }
        setState(stateForError(error, [SCOPES.ownRequest]));
      });
  }, [api, rideRequestId, read]);

  /* ------------------------------------------------------------------- the states before a read */

  if (rideRequestId === undefined) {
    /*
     * Reached by a link that names this screen without a ride.
     *
     * DSP-04 has no listing — deliberately, so a holder of one `demandId` cannot enumerate a
     * tenant's dispatches — so there is nothing to look up and nothing to guess.
     */
    return (
      <StateScreen
        screen="search"
        chip={{ class: "unknown", label: "no ride" }}
        title="This link names no ride"
        sub="A dispatch is read by the booking it belongs to, and this address carries none. There is deliberately no way to list them, so there is nothing to look up. Start again from Where to."
      />
    );
  }

  if (!props.scopes.has(SCOPES.dispatchProcess)) {
    return (
      <StateScreen
        screen="search"
        state={{ kind: "forbidden", missingScopes: [SCOPES.dispatchProcess] }}
      />
    );
  }

  if (state.kind !== "ready") {
    return (
      <StateScreen
        screen="search"
        state={state}
        onRetry={() => read(false)}
        purpose="Checking how the search for your driver is going."
      />
    );
  }

  /*
   * 404: the booking exists and its dispatch has not started, or there is no such dispatch.
   *
   * DSP-04 answers the two alike and says so in its own contract, so this screen says both rather
   * than picking one. The `pending` note is `4 Confirming`'s dashed outline, which is this
   * application's vocabulary for a thing that is not settled yet.
   */
  if (notStarted || process === undefined) {
    return (
      <PhoneShell
        screen="search"
        hero={<SearchHero />}
        back={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
        safety={{ armed: false }}
        chip={{ class: "unknown", label: "not started yet" }}
        title="The search hasn't started"
        sub="Your booking is in. Nothing has been dispatched for it yet."
        flag="wording not in the design"
        actions={[
          { label: "Check again", glyph: "refresh", primary: true, onPress: () => read(false) },
        ]}
      >
        <Note
          kind="pending"
          text="A dispatch that hasn't started and one that doesn't exist answer the same way, so this app can't tell you which of the two this is."
        />
      </PhoneShell>
    );
  }

  /*
   * The ride request was cancelled — by the button below, or from another device.
   *
   * Checked before the search's own status, because it is the stronger fact: a dispatch row can sit
   * at `finding_candidates` for the moment between the cancel landing on MOB-03 and the orchestrator
   * hearing about it, and "Finding your driver" over a cancelled booking is the one sentence on this
   * screen that would be a lie about something that definitely happened.
   */
  if (request?.status === "cancelled") {
    return (
      <PhoneShell
        screen="search"
        hero={<Hero kind="none" />}
        back={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
        safety={{ armed: false }}
        chip={{ class: "closed", label: "cancelled" }}
        title="This booking was cancelled"
        sub={
          request.cancellation === undefined
            ? "Nothing was charged."
            : `Cancelled ${request.cancellation.cancelledAt} · ${request.cancellation.reasonCode}. Nothing was charged.`
        }
        flag="wording not in the design"
        actions={[
          { label: "Get a new price", glyph: "refresh", primary: true, onPress: props.onBack },
        ]}
      />
    );
  }

  /*
   * `5b No driver` carries no staleness, and that is a fact about the record rather than an
   * omission.
   *
   * `completed` and `failed` are terminal in DSP-04's own domain, so the screen stops re-reading
   * when it reaches one — and an answer about a row that cannot move again does not go out of date
   * however long a rider looks at it. Only the search itself can be stale, which is why `freshness`
   * is computed below this branch rather than above it.
   */
  if (exhausted) {
    return (
      <NoDriver
        process={process}
        products={products}
        quotes={quotes}
        unpriced={unpriced}
        bookedProductId={bookedProductId}
        bookedCurrency={request?.quote.fare.currency}
        canQuote={props.scopes.has(SCOPES.quote)}
        onQuoteChosen={props.onQuoteChosen}
        onBack={props.onBack}
      />
    );
  }

  /* --------------------------------------------------------------------------- 5 Finding ------- */

  const freshness = freshnessOf(observedAt ?? tick, tick);
  const countdown = countdownState(process.deadlineAt, tick);
  const asking = askingNear(pickup);
  const cancellable =
    props.scopes.has(SCOPES.ownRequest) && !cancelling && request?.status === "requested";

  return (
    <PhoneShell
      screen="search"
      hero={<SearchHero />}
      back={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
      safety={{ armed: false }}
      /*
       * `finding_candidates · try 1 of 3`, in the design's `active` pill.
       *
       * DSP-04's own status word, as the design writes it. When the answer has gone stale the class
       * drops to `unknown` — the dashed pill with a literal `?` — because a blue `active` pill over
       * an answer this application could not refresh is the over-claim R-09, R-18 and R-23 each
       * record from a different screen.
       */
      chip={
        freshness.stale
          ? { class: "unknown", label: searchChip(process).label }
          : searchChip(process)
      }
      title="Finding your driver"
      sub={asking.sub}
      {...(asking.designed && !freshness.stale
        ? {}
        : { flag: findingFlag(asking.designed, freshness.stale, freshness.label) })}
      actions={[
        {
          label: cancelling ? "Cancelling…" : "Cancel for free",
          glyph: "close",
          primary: false,
          ...(cancellable
            ? { onPress: cancel }
            : {
                unavailable: cancelling
                  ? "Your cancellation is on its way."
                  : !props.scopes.has(SCOPES.ownRequest)
                    ? `Your sign-in doesn't allow cancelling this ride. It needs ${SCOPES.ownRequest}.`
                    : "Your booking couldn't be read, so there is no version to cancel against. Cancelling without one could cancel a ride that has already changed.",
              }),
        },
      ]}
    >
      {/*
       * `Search time left 1:48` — the design's one key/value row, in its own `rowsBox`.
       *
       * The value is the countdown while the deadline is ahead of this phone's clock, and words when
       * it is not. `searchTimeLeft` carries the three cases; the note below says what the second one
       * means, because "none left" on a screen whose chip still reads `finding_candidates` is a
       * contradiction a rider is owed an explanation for.
       */}
      <Rows rows={[{ key: "Search time left", value: searchTimeLeft(countdown) }]} />

      {countdown.kind === "passed" ? (
        <Note
          kind="pending"
          text="The time this search was given has run out, and the dispatch hasn't recorded that yet. Either the last round is still being counted, or this phone's clock is ahead of ours."
        />
      ) : null}

      {countdown.kind === "unreadable" ? (
        <Absent words="the dispatch deadline could not be read" />
      ) : null}

      {freshness.stale ? (
        <Note
          kind="pending"
          text={`This is how the search stood ${freshness.label}. We haven't been able to check since.`}
        />
      ) : null}
    </PhoneShell>
  );
}

/**
 * Why `5 Finding`'s wording is not the design's, when it is not.
 *
 * Two independent reasons and the flag has to be able to carry either or both: GEO-02 did not name
 * the pickup, so the sub-line lost its first half; or the answer is stale, so the chip is the dashed
 * pill rather than the designed blue one. Said apart, because only one of them is a gap and the
 * other is a network.
 */
function findingFlag(placeNamed: boolean, stale: boolean, age: string): string {
  const reasons = [
    placeNamed ? undefined : "your pickup couldn't be named",
    stale ? `last checked ${age}` : undefined,
  ].filter((reason): reason is string => reason !== undefined);
  return `wording not in the design — ${reasons.join("; ")}`;
}

/* ------------------------------------------------------------------------------ 5b No driver --- */

/**
 * `5b No driver` — the exhausted search, with one cheaper class offered instead.
 *
 * Its own component rather than another branch above, because it needs five things `5 Finding` does
 * not and would otherwise read as a second screen hidden inside the first.
 *
 * The design draws **one** alternative row — `Try Standard`, `4 seats · 2 bags`, `KES 1,450`,
 * `4 min` — so one is drawn: the cheapest class MOB-02 priced that is not the class that failed.
 * `4 min` is the absence `2 Choose` already measures and is drawn the same way, because nothing
 * publishes how long until a driver of a class could reach a rider.
 */
function NoDriver(props: {
  readonly process: DispatchProcess;
  readonly products: readonly RideProduct[];
  readonly quotes: ReadonlyMap<string, RideQuote>;
  readonly unpriced: ReadonlySet<string>;
  readonly bookedProductId: string | undefined;
  /** The currency MOB-02 priced the booked class in, so `cheaper` is compared inside one of them. */
  readonly bookedCurrency: string | undefined;
  readonly canQuote: boolean;
  readonly onQuoteChosen: (quoteId: string) => void;
  readonly onBack: () => void;
}): ReactNode {
  const { process, products, quotes, bookedProductId, bookedCurrency } = props;

  const className =
    products.find((product) => product.productId === bookedProductId)?.displayName ?? null;

  const told = exhaustion({
    failureReason: process.failureReason,
    attempts: process.attempt,
    className,
  });

  /*
   * The cheapest class MOB-02 priced, among the classes that are not the one that failed.
   *
   * Compared in minor units within one currency and never across two: two figures in different
   * currencies have no "cheaper" between them, and `AGENTS.md` forbids the coercion that would
   * produce one. A tenant whose catalogue priced a route in two currencies therefore offers only
   * those matching the booked fare's, which is the honest subset rather than an ordering across
   * them.
   */
  const alternative = useMemo((): { product: RideProduct; quote: RideQuote } | undefined => {
    const priced = products
      .filter((product) => product.lifecycle === "active")
      .filter((product) => product.productId !== bookedProductId)
      .flatMap((product) => {
        const quote = quotes.get(product.productId);
        return quote === undefined ? [] : [{ product, quote }];
      });
    /*
     * The currency to compare inside: the booked class's when MOB-03's record could be read, and
     * otherwise the first priced row's. Never a comparison across two, and never a default: a
     * currency chosen here would be this application deciding which of a tenant's prices counts.
     */
    const currency = bookedCurrency ?? priced[0]?.quote.fare.currency;
    if (currency === undefined) return undefined;
    return [...priced]
      .filter((row) => row.quote.fare.currency === currency)
      .sort((left, right) => left.quote.fare.totalMinor - right.quote.fare.totalMinor)[0];
  }, [products, quotes, bookedProductId, bookedCurrency]);

  const actions: ActionProps[] = [
    {
      /*
       * `Try Standard instead` — the design's label, naming the class it is offering.
       *
       * Drawn in every case, because nothing may be removed, and unpressable with the reason when
       * there is nothing to offer. Three different reasons, said apart: the sign-in cannot mint a
       * price, no other class came back with one, or — for a search that ran out of *time* rather
       * than of drivers — a cheaper class is not what was wrong.
       */
      label:
        alternative === undefined
          ? "Try another class"
          : `Try ${alternative.product.displayName} instead`,
      glyph:
        alternative === undefined ? "local_taxi" : productGlyph(alternative.product.travelMode),
      primary: true,
      ...(alternative !== undefined && told.offerAnotherClass
        ? { onPress: (): void => props.onQuoteChosen(alternative.quote.id) }
        : {
            unavailable: !props.canQuote
              ? `Your sign-in doesn't allow pricing another class. It needs ${SCOPES.quote}.`
              : !told.offerAnotherClass
                ? "This search ran out of time rather than out of drivers, so a cheaper class isn't what went wrong. Get a fresh price for the class you wanted."
                : "No other class came back with a price, so there is nothing to offer instead.",
          }),
    },
    {
      label: "Schedule for later",
      glyph: "event",
      primary: false,
      /*
       * Drawn, and it cannot work — the same absence `1 Where to`'s `Schedule a ride` carries, in
       * the same words, because it is the same missing field on the same operation.
       */
      unavailable:
        "Nothing published accepts a ride for later: POST /v1/ride-requests takes a quoteId and no time.",
    },
  ];

  return (
    <PhoneShell
      screen="search"
      hero={<Hero kind="none" />}
      back={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
      safety={{ armed: false }}
      /* The design's own `failed` / `No driver found`, which names the outcome rather than the saga. */
      chip={searchChip(process)}
      title={told.title}
      sub={told.sub}
      {...(told.designed ? {} : { flag: noDriverFlag() })}
      actions={actions}
    >
      {alternative === undefined ? (
        /*
         * Three different reasons there is no row, said apart.
         *
         * `unpriced` is the set of classes MOB-02 refused, so a screen that has been refused is not
         * the same as one still waiting — and neither is the same as a sign-in that cannot ask.
         */
        <Absent
          words={
            !props.canQuote
              ? `pricing another class needs ${SCOPES.quote}`
              : props.unpriced.size > 0
                ? "no other class came back with a price"
                : "pricing the other classes"
          }
        />
      ) : (
        <div className="options">
          <button
            type="button"
            className="option"
            onClick={() => props.onQuoteChosen(alternative.quote.id)}
          >
            <span className="glyph option__glyph" aria-hidden="true">
              {productGlyph(alternative.product.travelMode)}
            </span>
            <span className="option__text">
              {/* `Try Standard` — the design writes the row's own name with its verb on it. */}
              <span className="option__name">{`Try ${alternative.product.displayName}`}</span>
              <span className="option__attributes">{productAttributes(alternative.product)}</span>
            </span>
            <span className="option__right">
              <span className="option__price">{quoteMoney(alternative.quote.fare)}</span>
              {/*
               * `4 min` — the same measured absence `2 Choose` draws three of. Nothing publishes
               * how long until a driver of a class could reach a rider, and a number here would be
               * a rider choosing a class to pay for on a figure nobody computed.
               */}
              <Absent words="no pickup eta" className="option__eta" />
            </span>
          </button>
        </div>
      )}
    </PhoneShell>
  );
}

/**
 * Why `5b`'s wording is not the design's, when it is not.
 *
 * One reason rather than `findingFlag`'s two, and the asymmetry is the record's: an exhausted
 * dispatch cannot go stale, because a terminal process does not move and this screen stops asking.
 * So the only way `5b` departs from the design is the reason the search ended — `no_supply` is the
 * one the design draws, and the class name has to have been joined for its heading to be the
 * design's sentence.
 */
function noDriverFlag(): string {
  return "wording not in the design — the search's own reason is not the one the design draws";
}
